import React, { useEffect, useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import LoadingMark from '../../../components/ui/LoadingMark';
import { ModalShell } from '../../../components/ui/ModalFrame';
import { MODAL_LAYER } from '../../../constants/modalLayers';
import { MODAL_DIALOG_ACTIONS_END } from '../../../constants/layoutSpacing';
import licenseService from '../../../services/licenseService';
import { formatCurrencyBRL } from '../../../utils/formatCurrencyBRL';
import { subscriptionChargeLabel } from '../../../utils/subscriptionChargeLabel';

const CancelSubscriptionConfirmDialog = ({ open, licenseName, onCancel, onConfirm, loading }) => {
  if (!open) return null;

  return (
    <ModalShell
      zClass={MODAL_LAYER.standard}
      backdropClassName="bg-black/50"
      panelClassName="bg-card rounded-lg max-w-md w-full p-6 shadow-elevation-lg"
    >
      <p className="text-base text-foreground mb-6">
        Cancelar a assinatura de{' '}
        <span className="font-semibold">{licenseName || 'seu plano'}</span>? As
        cobranças futuras serão interrompidas e seu acesso permanece até o fim do
        período já pago.
      </p>

      <div className={MODAL_DIALOG_ACTIONS_END}>
        <Button variant="outline" onClick={onCancel} disabled={loading}>
          Voltar
        </Button>
        <Button variant="default" onClick={onConfirm} loading={loading}>
          Confirmar cancelamento
        </Button>
      </div>
    </ModalShell>
  );
};

/**
 * @param {{ neoCoinsLabel?: string }} props
 */
const MySubscriptionPanel = ({ neoCoinsLabel }) => {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const loadSubscription = async () => {
    setLoading(true);
    setError(null);
    const response = await licenseService.getMySubscription();
    if (!response.success) {
      setError(response.error || 'Erro ao carregar assinatura');
      setSubscription(null);
    } else {
      setSubscription(response.subscription);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadSubscription();
  }, []);

  const handleCancel = async () => {
    setActionLoading(true);
    setMessage(null);
    setError(null);
    try {
      const response = await licenseService.cancelSubscription();
      if (!response?.success) {
        throw new Error(response?.error || 'Erro ao cancelar assinatura');
      }
      setMessage(response.message);
      setConfirmOpen(false);
      await loadSubscription();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div
        data-tour="tour-my-subscription"
        className="rounded-lg border border-border bg-card p-4 flex justify-center"
      >
        <LoadingMark size="xs" message="Carregando assinatura..." messageClassName="text-sm" />
      </div>
    );
  }

  if (!subscription) {
    return null;
  }

  const isCancelled = subscription.cancel_at_period_end;
  const statusLabel = isCancelled
    ? 'Cancelada — acesso até o fim do período'
    : subscription.status === 'authorized' || subscription.status === 'active'
      ? 'Ativa'
      : subscription.status;

  return (
    <div
      data-tour="tour-my-subscription"
      className="rounded-lg border border-border bg-card p-5 space-y-4"
    >
      <div className="flex items-start gap-3">
        <Icon name="Calendar" size={20} className="text-primary mt-0.5" />
        <div className="space-y-1 flex-1">
          <p className="font-semibold text-foreground">Minha assinatura</p>
          <p className="text-sm text-muted-foreground">
            {subscription.license_name} — {formatCurrencyBRL(subscription.amount)}/mês
          </p>
          <p className="text-xs text-muted-foreground">Status: {statusLabel}</p>
          {subscription.next_payment_date && !isCancelled && (
            <p className="text-xs text-muted-foreground">
              {subscriptionChargeLabel(subscription)}:{' '}
              {new Date(subscription.next_payment_date).toLocaleDateString('pt-BR')}
            </p>
          )}
          {neoCoinsLabel != null && (
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 pt-2">
              <Icon name="Coins" size={16} className="text-amber-500 shrink-0" aria-hidden />
              <span className="text-xs font-medium text-muted-foreground">Neo Coins</span>
              <span className="text-sm font-semibold tabular-nums text-foreground">
                {neoCoinsLabel}
              </span>
            </div>
          )}
        </div>
      </div>

      {message && <p className="text-sm text-success">{message}</p>}
      {error && <p className="text-sm text-destructive">{error}</p>}

      {!isCancelled && (
        <Button
          variant="outline"
          onClick={() => setConfirmOpen(true)}
          loading={actionLoading}
        >
          Cancelar assinatura
        </Button>
      )}

      <CancelSubscriptionConfirmDialog
        open={confirmOpen}
        licenseName={subscription.license_name}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={handleCancel}
        loading={actionLoading}
      />
    </div>
  );
};

export default MySubscriptionPanel;
